class UgcVideoCarousel extends HTMLElement {
  #activeIndex = 0;
  #tx = 0;
  #count = 0;
  #offset = 0;
  #abortController = null;

  #dragging = false;
  #dragStartX = 0;
  #dragStartTx = 0;
  #dragDelta = 0;

  connectedCallback() {
    this.wrapper = this.querySelector('.ugc-carousel__track-wrapper');
    this.track = this.querySelector('.ugc-carousel__track');
    const originalSlides = [...this.querySelectorAll('.ugc-carousel__slide')];
    this.prevBtn = this.querySelector('.ugc-carousel__prev');
    this.nextBtn = this.querySelector('.ugc-carousel__next');

    if (!this.track || !this.wrapper || originalSlides.length < 2) {
      this.slides = originalSlides;
      this.#count = originalSlides.length;
      this.#offset = 0;
      if (this.slides.length) {
        requestAnimationFrame(() => {
          this.#tx = this.#computeTx(0);
          this.#applyTx(false);
          this.#activate(0);
        });
      }
      return;
    }

    this.#count = originalSlides.length;
    this.#offset = this.#count;

    originalSlides.forEach((slide, i) => {
      const clone = slide.cloneNode(true);
      clone.removeAttribute('id');
      clone.dataset.clone = i;
      this.track.appendChild(clone);
    });

    const ref = this.track.firstChild;
    for (let i = 0; i < originalSlides.length; i++) {
      const clone = originalSlides[i].cloneNode(true);
      clone.removeAttribute('id');
      clone.dataset.clone = i;
      this.track.insertBefore(clone, ref);
    }

    this.slides = [...this.querySelectorAll('.ugc-carousel__slide')];

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.prevBtn?.addEventListener('click', () => this.#navigate(-1), { signal });
    this.nextBtn?.addEventListener('click', () => this.#navigate(1), { signal });

    this.track.addEventListener('transitionend', this.#onTransitionEnd, { signal });

    this.track.addEventListener('pointerdown', this.#onPointerDown, { signal });

    document.addEventListener('visibilitychange', this.#onVisibilityChange, { signal });

    requestAnimationFrame(() => {
      this.#goTo(this.#offset, false);
    });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  #computeTx(domIndex) {
    const slide = this.slides[domIndex];
    if (!slide) return 0;
    const wrapperW = this.wrapper.offsetWidth;
    const slideW = slide.offsetWidth;
    return wrapperW / 2 - slideW / 2 - slide.offsetLeft;
  }

  #applyTx(animate) {
    if (animate) {
      this.track.classList.add('is-animating');
      this.track.offsetHeight;
    } else {
      this.track.classList.remove('is-animating');
    }
    this.track.style.transform = `translateX(${this.#tx}px)`;
  }

  #goTo(domIndex, animate) {
    const targetTx = this.#computeTx(domIndex);
    if (domIndex !== this.#activeIndex) {
      this.#deactivate(this.#activeIndex);
    }
    this.#tx = targetTx;
    this.#activeIndex = domIndex;
    this.#applyTx(animate);
    this.#activate(domIndex);
  }

  #onTransitionEnd = (e) => {
    if (e.target !== this.track) return;
    this.#resetIfClone();
  };

  #resetIfClone() {
    const slide = this.slides[this.#activeIndex];
    if (!slide || slide.dataset.clone === undefined) return;

    const originalIndex = parseInt(slide.dataset.clone, 10);
    const realDomIndex = originalIndex + this.#offset;

    this.track.classList.add('is-teleporting');

    this.#deactivate(this.#activeIndex);
    this.track.classList.remove('is-animating');
    this.#tx = this.#computeTx(realDomIndex);
    this.track.style.transform = `translateX(${this.#tx}px)`;
    this.track.offsetHeight;
    this.#activeIndex = realDomIndex;
    this.#activate(realDomIndex);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.track.classList.remove('is-teleporting');
      });
    });
  }

  #navigate(direction) {
    const currentSlide = this.slides[this.#activeIndex];
    if (currentSlide?.dataset.clone !== undefined) {
      const origIdx = parseInt(currentSlide.dataset.clone, 10);
      const realDom = origIdx + this.#offset;

      this.track.classList.add('is-teleporting');

      this.#deactivate(this.#activeIndex);
      this.track.classList.remove('is-animating');
      this.#tx = this.#computeTx(realDom);
      this.track.style.transform = `translateX(${this.#tx}px)`;
      this.track.offsetHeight;
      this.#activeIndex = realDom;
      this.#activate(realDom);

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.track.classList.remove('is-teleporting');
        });
      });
    }

    const logicalIndex = this.#activeIndex - this.#offset;
    const newLogical = ((logicalIndex + direction) % this.#count + this.#count) % this.#count;

    if (direction > 0 && newLogical < logicalIndex) {
      this.#goTo(this.#offset + this.#count + newLogical, true);
    } else if (direction < 0 && newLogical > logicalIndex) {
      this.#goTo(newLogical, true);
    } else {
      this.#goTo(newLogical + this.#offset, true);
    }
  }

  #activate(domIndex) {
    const slide = this.slides[domIndex];
    if (!slide) return;
    slide.classList.add('is-active');
    const video = slide.querySelector('video');
    if (video) video.play().catch(() => {});
  }

  #deactivate(domIndex) {
    const slide = this.slides[domIndex];
    if (!slide) return;
    slide.classList.remove('is-active');
    const video = slide.querySelector('video');
    if (video) {
      video.pause();
      video.currentTime = 0;
      video.muted = true;
    }
    const soundBtn = slide.querySelector('.ugc-carousel__sound-btn');
    if (soundBtn) soundBtn.classList.remove('is-unmuted');
  }

  #onPointerDown = (e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    this.#dragging = false;
    this.#dragDelta = 0;
    this.#dragStartX = e.clientX;
    this.#dragStartTx = this.#tx;

    this.track.classList.remove('is-animating');
    this.track.setPointerCapture(e.pointerId);

    const signal = this.#abortController?.signal;
    this.track.addEventListener('pointermove', this.#onPointerMove, { signal });
    this.track.addEventListener('pointerup', this.#onPointerUp, { signal });
    this.track.addEventListener('pointercancel', this.#onPointerUp, { signal });
  };

  #onPointerMove = (e) => {
    this.#dragDelta = e.clientX - this.#dragStartX;
    if (Math.abs(this.#dragDelta) > 4) this.#dragging = true;
    if (!this.#dragging) return;

    this.#tx = this.#dragStartTx + this.#dragDelta;
    this.track.style.transform = `translateX(${this.#tx}px)`;
  };

  #onPointerUp = (e) => {
    this.track.removeEventListener('pointermove', this.#onPointerMove);
    this.track.removeEventListener('pointerup', this.#onPointerUp);
    this.track.removeEventListener('pointercancel', this.#onPointerUp);
    this.track.releasePointerCapture(e.pointerId);

    if (this.#dragging) {
      const closest = this.#findClosestSlide();
      this.#goTo(closest, true);
      setTimeout(() => { this.#dragging = false; }, 0);
      return;
    }

    const el = document.elementFromPoint(e.clientX, e.clientY);
    if (!el) return;

    const soundBtn = el.closest('.ugc-carousel__sound-btn');
    if (soundBtn) {
      const video = soundBtn.closest('.ugc-carousel__slide')?.querySelector('video');
      if (!video) return;
      video.muted = !video.muted;
      soundBtn.classList.toggle('is-unmuted', !video.muted);
      return;
    }

    const slide = el.closest('.ugc-carousel__slide');
    if (!slide) return;
    const i = this.slides.indexOf(slide);
    if (i !== -1 && i !== this.#activeIndex) this.#goTo(i, true);
  };

  #findClosestSlide() {
    const wrapperCenter = this.wrapper.offsetWidth / 2;
    let closestIndex = this.#activeIndex;
    let closestDist = Infinity;

    this.slides.forEach((slide, i) => {
      const slideCenter = slide.offsetLeft + slide.offsetWidth / 2 + this.#tx;
      const dist = Math.abs(slideCenter - wrapperCenter);
      if (dist < closestDist) {
        closestDist = dist;
        closestIndex = i;
      }
    });

    return closestIndex;
  }

  #onVisibilityChange = () => {
    const slide = this.slides[this.#activeIndex];
    if (!slide) return;
    const video = slide.querySelector('video');
    if (!video) return;

    if (document.hidden) {
      video.pause();
    } else {
      video.play().catch(() => {});
    }
  };
}

if (!customElements.get('ugc-video-carousel')) {
  customElements.define('ugc-video-carousel', UgcVideoCarousel);
}
